import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Box, Button, Grid, MenuItem, Select, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import LeadFieldPermission from "./LeadFieldPermission";
import SaveIcon from "@mui/icons-material/Save";
import ButtonBack from "../utils/ButtonBack";
import ThemedSelect from "../utils/ThemedSelect";
import ErrorModal from "../utils/ErrorModal";

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#D5DEE7";
const PRIMARY = "#0B5C8A";
const lbl = { display: "block", marginBottom: 6, fontFamily: FONT, fontSize: 12, fontWeight: 600, color: "#3D4C60" };
const inp = (readOnly) => ({
  display: "block",
  width: "100%",
  boxSizing: "border-box",
  height: 42,
  padding: "0 12px",
  margin: "0 0 14px",
  background: readOnly ? "#F7F9FC" : "#FFFFFF",
  border: `1px solid ${FIELD_EDGE}`,
  borderRadius: 10,
  fontFamily: readOnly ? MONO : FONT,
  fontSize: readOnly ? 13 : 14,
  color: readOnly ? "#5A6779" : INK,
  outline: "none",
});
const card = (locked) => ({
  width: "100%",
  boxSizing: "border-box",
  margin: 0,
  padding: "18px 20px",
  background: "#FFFFFF",
  border: `1px solid ${LINE}`,
  borderRadius: 14,
  boxShadow: "none",
  opacity: locked ? 0.5 : 1,
  pointerEvents: locked ? "none" : "auto",
});
const h2 = { fontFamily: FONT, fontSize: 18, fontWeight: 800, letterSpacing: "-0.02em", color: INK };
const h3 = { fontFamily: FONT, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.11em", textTransform: "uppercase", color: MUTED };
const primaryBtn = {
  height: 42,
  px: 2.5,
  margin: "0 !important",
  gap: 0.75,
  borderRadius: "10px !important",
  background: `${PRIMARY} !important`,
  color: "#FFFFFF !important",
  border: "0 !important",
  fontFamily: FONT,
  fontSize: 14,
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { background: "#084767 !important" },
  "&.Mui-disabled": { opacity: 0.55 },
};
const chip = (on, locked) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
  boxSizing: "border-box",
  minHeight: 42,
  margin: 0,
  padding: "8px 12px",
  background: on ? (locked ? "#F1F4F8" : "#EAF2FB") : "#FFFFFF",
  border: `1px solid ${on ? (locked ? LINE : "#9FC3EA") : LINE}`,
  borderRadius: 10,
  cursor: locked ? "not-allowed" : "pointer",
  fontFamily: FONT,
  fontSize: 13,
  fontWeight: on ? 600 : 500,
  color: on ? INK : "#4A5A6E",
  textAlign: "left",
  lineHeight: 1.3,
});
const box = (on, locked) => ({
  flex: "0 0 16px",
  width: 16,
  height: 16,
  boxSizing: "border-box",
  borderRadius: 5,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 11,
  fontWeight: 800,
  lineHeight: 1,
  color: "#FFFFFF",
  background: on ? (locked ? "#8494A8" : PRIMARY) : "#FFFFFF",
  border: `1.5px solid ${on ? (locked ? "#8494A8" : PRIMARY) : "#C3CDD9"}`,
});

const STATIC_FIELDS = [
  { name: "name", label: "Lead Name", type: "text" },
  { name: "phone", label: "Phone", type: "text" },
  { name: "alternatePhone", label: "Alternate Phone", type: "text" },
  { name: "isWhatsapp", label: "Is WhatsApp", type: "boolean" },
  { name: "isAlternateWhatsapp", label: "Alternate WhatsApp", type: "boolean" },

  { name: "email", label: "Email", type: "email" },
  { name: "leadSource", label: "Lead Source", type: "text" },

  { name: "currentCity", label: "Current City", type: "text" },
  { name: "currentCountry", label: "Current Country", type: "text" },

  { name: "requirement", label: "Requirement", type: "text" },

  {
    name: "preferredPropertyType",
    label: "Preferred Property Type",
    type: "text",
  },

  { name: "minBudget", label: "Minimum Budget", type: "number" },
  { name: "maxBudget", label: "Maximum Budget", type: "number" },

  { name: "timelineToBuy", label: "Timeline To Buy", type: "text" },
  { name: "purpose", label: "Purpose", type: "text" },

  // DATE FIELDS
  { name: "nextFollowUp", label: "Next Follow Up", type: "date" },
  { name: "lastFollowUp", label: "Last Follow Up", type: "date" },
  { name: "remark", label: "Comment", type: "text" },

  // UTM FIELDS
  { name: "utmSource", label: "UTM Source", type: "text" },
  { name: "utmMedium", label: "UTM Medium", type: "text" },
  { name: "utmCampaign", label: "UTM Campaign", type: "text" },
  { name: "utmTerm", label: "UTM Term", type: "text" },
  { name: "utmContent", label: "UTM Content", type: "text" },

  { name: "leadScore", label: "Lead Score", type: "number" },
  { name: "aiScore", label: "AI Score", type: "number" },
];

const FIELD_TYPES = [
  "TEXT",
  "NUMBER",
  "DATE",
  "BOOLEAN",
  "SINGLE_SELECT",
  "MULTI_SELECT",
];

const EditStructure = () => {
  const { projectId } = useParams();
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  const [customFields, setCustomFields] = useState([]);
  const mandatory = [
    "name",
    "phone",
    "alternatePhone",
    "isWhatsapp",
    "isAlternateWhatsapp",
    "email",
    "nextFollowUp",
    "lastFollowUp",
  ];
  const [selectedFields, setSelectedFields] = useState([]);

  const [fieldForm, setFieldForm] = useState({
    fieldName: "",
    label: "",
    type: "TEXT",
    options: [],
  });

  const [optionInput, setOptionInput] = useState("");
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMsg, setModalMsg] = useState("");
  const [modalType, setModalType] = useState("success");
  const afterClose = useRef(null);

  const showModal = (msg, type, onClose = null) => {
    setModalMsg(msg);
    setModalType(type);
    afterClose.current = onClose;
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    const next = afterClose.current;
    afterClose.current = null;
    if (next) next();
  };

  useEffect(() => {
    if (!projectId) return;

    const fetchStructure = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/lead-structure?projectId=${projectId}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
        const defaultFields = res.data?.fieldNames || [];

        const customFieldNames =
          res.data?.customFields?.map((f) => f.fieldName) || [];

        setSelectedFields(
          Array.from(
            new Set([...mandatory, ...defaultFields, ...customFieldNames]),
          ),
        );
      } catch (error) {
        console.log(error);
      }
    };
    fetchStructure();

    const fetchFields = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/custom-field?projectId=${projectId}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
        setCustomFields(res.data);
      } catch (error) {
        console.log(error);
      }
    };
    fetchFields();
  }, [projectId]);

  /* ---------------- CUSTOM FIELD LOGIC ---------------- */
  const addOption = () => {
    if (!optionInput.trim()) return;
    setFieldForm({
      ...fieldForm,
      options: [...fieldForm.options, { value: optionInput }],
    });
    setOptionInput("");
  };

  const addCustomField = async () => {
    try {
      const payload = {
        projectId,
        ...fieldForm,
        fieldName: fieldForm.label.trim().toLowerCase().replace(/\s+/g, "_"),
      };

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/custom-field`,
        payload,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );

      setFieldForm({ fieldName: "", label: "", type: "TEXT", options: [] });
      setOptionInput("");

      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/custom-field?projectId=${projectId}`,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );

      setCustomFields(res.data);
    } catch (error) {
      console.log(error);
      showModal(
        error?.response?.data?.Exception || "Failed to save custom field",
        "error",
      );
    }
  };

  /* ---------------- STRUCTURE LOGIC ---------------- */
  const toggleField = (name) => {
    if (mandatory.includes(name)) return;
    setSelectedFields((prev) =>
      prev.includes(name) ? prev.filter((f) => f !== name) : [...prev, name],
    );
  };

  const submitStructure = async () => {
    try {
      setLoading(true);
      const STATIC_FIELD_NAMES = new Set(STATIC_FIELDS.map((f) => f.name));

      const finalFields = Array.from(new Set([...mandatory, ...selectedFields]));

      const payload = finalFields.map((fieldName) => ({
        projectId,
        fieldName,
        fieldType: STATIC_FIELD_NAMES.has(fieldName) ? "STATIC" : "CUSTOM",
      }));

      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/lead-structure`,
        payload,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );

      showModal("Lead structure updated", "success", () => setStep(2));
    } catch (error) {
      console.log(error);
      showModal(
        error?.response?.data?.Exception || "Failed to update lead structure",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const saveLabel = (
    <>
      <SaveIcon sx={{ fontSize: 17 }} /> Save Lead Form
      Structure
    </>
  );

  return (
    <div style={{ fontFamily: FONT, color: INK, textAlign: "left" }}>
      {step === 1 && (
        <div style={{ padding: 0 }}>
          {/* <h3>Edit Lead Structure</h3> */}
          <Typography sx={{ fontFamily: FONT, fontSize: 27, fontWeight: 800, letterSpacing: "-0.028em", color: INK, mb: 0.5 }}>
            Edit Lead Structure
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: "#4A5A6E", mb: 2 }}>
            Step 1 of 2 · Change which fields this project's lead form captures.
          </Typography>

          <Typography variant="h5" sx={{ ...h2, mt: 1, mb: 1.25 }}>
            Add Custom Field
          </Typography>
          <div className="add-forms" style={card(false)}>
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <label style={lbl}>Label</label>
                <input
                  placeholder="Label"
                  value={fieldForm.label}
                  onChange={(e) =>
                    setFieldForm({ ...fieldForm, label: e.target.value })
                  }
                  style={inp(false)}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <label style={lbl}>Field Name</label>
                <input
                  placeholder="Field Name"
                  value={fieldForm.label.trim().toLowerCase().replace(/\s+/g, "_")}
                  readOnly
                  style={inp(true)}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <label style={lbl}>Type</label>
                <ThemedSelect
                  name="type"
                  value={fieldForm.type}
                  onChange={(e) =>
                    setFieldForm({ ...fieldForm, type: e.target.value })
                  }
                  options={FIELD_TYPES.map((t) => ({ value: t, label: t }))}
                  sx={{ mb: 1.75 }}
                />
              </Grid>
            </Grid>

            {(fieldForm.type === "SINGLE_SELECT" ||
              fieldForm.type === "MULTI_SELECT") && (
              <div style={{ padding: "12px 14px", marginBottom: 14, background: "#F7F9FC", border: `1px solid ${LINE}`, borderRadius: 12 }}>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <input
                    placeholder="Option value"
                    value={optionInput}
                    onChange={(e) => setOptionInput(e.target.value)}
                    style={{ ...inp(false), flex: "1 1 200px", width: "auto", margin: 0 }}
                  />
                  <Button
                    className="btn-contained"
                    onClick={addOption}
                    sx={{ ...primaryBtn, marginTop: "0 !important", background: "#FFFFFF !important", color: `${INK} !important`, border: `1px solid ${FIELD_EDGE} !important`, "&:hover": { background: "#EAF2FB !important" } }}
                  >
                    + Add Option
                  </Button>
                </div>

                <ul style={{ display: "flex", flexWrap: "wrap", gap: 6, listStyle: "none", margin: fieldForm.options.length ? "10px 0 0" : 0, padding: 0 }}>
                  {fieldForm.options.map((o, i) => (
                    <li
                      key={i}
                      style={{ padding: "3px 10px", borderRadius: 999, background: "#EAF2FB", border: "1px solid #C9DEF3", fontSize: 12.5, fontWeight: 600, color: "#1B4F82" }}
                    >
                      {o.value}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <Box
              sx={{
                display: "flex",
                justifyContent: "end",
                alignItems: "center",
              }}
            >
              <Button className="btn-contained" onClick={addCustomField} sx={primaryBtn}>
                <SaveIcon sx={{ fontSize: 17 }} /> Save Custom Field
              </Button>
            </Box>
          </div>

          <Typography variant="h5" sx={{ ...h2, mt: 3, mb: 1.25 }}>
            Form Structure
          </Typography>
          <div className="add-forms" style={card(false)}>
            <Typography variant="h6" sx={{ ...h3, mb: 1.25 }}>
              Static Fields
            </Typography>
            <Grid container spacing={{ xs: 1, sm: 1.25 }}>
              {STATIC_FIELDS.map((f) => {
                const isMandatory = mandatory.includes(f.name);
                const isSelected =
                  isMandatory || selectedFields.includes(f.name);

                return (
                  <Grid size={{ xs: 6, sm: 3, md: 2 }} key={f.name} textAlign="center">
                    <label
                      title={
                        isMandatory
                          ? "This field is required and cannot be removed"
                          : ""
                      }
                      className={`project-checkbox ${isSelected ? "checkbox-active" : ""} ${
                        isMandatory ? "checkbox-locked" : ""
                      }`}
                      style={chip(isSelected, isMandatory)}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isMandatory}
                        onChange={() => toggleField(f.name)}
                        style={{ width: "auto", display: "none" }}
                      />
                      <span style={box(isSelected, isMandatory)}>{isSelected ? "✓" : ""}</span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        {f.label}
                        {isMandatory && (
                          <span style={{ color: "#C42B24", marginLeft: 3 }}>*</span>
                        )}
                      </span>
                    </label>
                  </Grid>
                );
              })}
            </Grid>

            <Typography variant="h6" sx={{ ...h3, mt: 2.5, mb: 1.25 }}>
              Custom Fields
            </Typography>
            <Grid container spacing={{ xs: 1, sm: 1.25 }}>
              {customFields.map((f) => (
                <Grid
                  size={{ xs: 6, sm: 3, md: 2 }}
                  key={f.fieldId}
                  textAlign={"center"}
                >
                  <label
                    className={`project-checkbox ${
                      selectedFields.includes(f.fieldName)
                        ? "checkbox-active"
                        : ""
                    }`}
                    style={chip(selectedFields.includes(f.fieldName), false)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedFields.includes(f.fieldName)}
                      onChange={() =>
                        setSelectedFields((prev) =>
                          prev.includes(f.fieldName)
                            ? prev.filter((x) => x !== f.fieldName)
                            : [...prev, f.fieldName],
                        )
                      }
                      style={{ width: "auto", display: "none" }}
                    />
                    <span style={box(selectedFields.includes(f.fieldName), false)}>
                      {selectedFields.includes(f.fieldName) ? "✓" : ""}
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>{f.label}</span>
                  </label>
                </Grid>
              ))}
            </Grid>

            <div style={{ height: 1, background: "#EDF1F5", margin: "18px 0 14px" }}></div>
            <Box
              className="btn-container"
              mb={0}
              sx={{ display: { xs: "none", sm: "flex" }, justifyContent: "flex-end", alignItems: "center", gap: 1.25 }}
            >
              <ButtonBack handleBack={() => navigate("/dashboard/projects")} />
              <Button
                className="btn-contained"
                onClick={submitStructure}
                sx={{ ...primaryBtn, width: { xs: "100% !important", sm: "auto !important" } }}
                disabled={loading}
              >
                {loading ? "Saving..." : saveLabel}
              </Button>
            </Box>
            <Box mb={0} sx={{ display: { xs: "flex", sm: "none" }, flexDirection: "column", gap: 1.25 }}>
              <Button
                className="btn-contained"
                onClick={submitStructure}
                fullWidth
                disabled={loading}
                sx={{ ...primaryBtn, height: 44 }}
              >
                {loading ? "Saving..." : saveLabel}
              </Button>
              <ButtonBack
                fullWidth={true}
                handleBack={() => navigate("/dashboard/projects")}
              />
            </Box>
          </div>
        </div>
      )}
      {step === 2 && <LeadFieldPermission projectId={projectId} />}

      <ErrorModal
        open={modalOpen}
        handleClose={handleModalClose}
        msg={modalMsg}
        type={modalType}
      />
    </div>
  );
};

export default EditStructure;